import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useRef, useState, useEffect } from "react";
import CalendarToolbar from "./CalendarToolbar";
import ActivityHoverCard from "./ActivityHoverCard";
import { useDispatch, useSelector } from "react-redux";
import { ContentTypes } from "../constants/action-types";
import ActivityAction from "../actions/ActivityAction";
import { markAsDoneActivity, updateActivity } from "../api/ProjectApi";
import { addMinutes, addDays, addHours, format, isSameDay, isSameYear } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import {
  PhoneIcon,
  CalendarDaysIcon,
  ClockIcon,
  FlagIcon,
  FaceSmileIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { ActivityTypes } from "../constants/action-types";
import { CalendarOptions, EventDropArg } from "@fullcalendar/core";
import Avatar from "../components/Avatar";
import LayoutAction from "../actions/LayoutAction";
import { useTranslation } from "react-i18next";
import { getGoogleCalendarEvents } from "../api/GoogleApi";
import { getCurrentGoogleOwner } from "../api/AppLinkApi";
import RuleAction from "../actions/RuleAction";
import { getBaseUrl } from "../api/api";
import { useQuery } from "@tanstack/react-query";
import { searchMessages } from "../api/InboxApi";
import { fromJS } from "immutable";

type EventResizeArg = Parameters<NonNullable<CalendarOptions["eventResize"]>>[0];

type CalendarViewType = "dayGridMonth" | "timeGridWeek" | "timeGridDay";

interface CalendarViewProps {
  currentSize: any;
  projectId: string;
  onSearchTasks: (args: any) => any;
  initialView?: CalendarViewType;
  allowedViews?: CalendarViewType[];
  hideViewTabs?: boolean;
  calendarHref?: string;
  height?: string | number;
  fullHeight?: boolean;
  onCalendarClick?: () => void;
}

export default function CalendarView({
  projectId,
  onSearchTasks,
  initialView = "timeGridWeek",
  allowedViews,
  hideViewTabs,
  calendarHref,
  height,
  fullHeight,
  onCalendarClick,
}: CalendarViewProps) {
  const dispatch = useDispatch();
  const calendarRef = useRef<any>(null);
  const [currentView, setCurrentView] = useState<CalendarViewType>(initialView);
  const [currentRangeLabel, setCurrentRangeLabel] = useState<string>("");
  const [hoveredEvent, setHoveredEvent] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [updatingEvents, setUpdatingEvents] = useState<Set<string>>(new Set());
  const [pendingEventTimes, setPendingEventTimes] = useState<
    Record<string, { start: Date; end: Date }>
  >({});

  const [dateRange, setDateRange] = useState<any>({});
  const hoverCardRef = useRef<HTMLDivElement>(null);
  const googleFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [googleEvents, setGoogleEvents] = useState<any[]>([]);

  const getRoundedTime = () => {
    const now = new Date();
    const hours = now.getHours();

    return format(new Date().setHours(hours, 0, 0, 0), "HH:mm:ss");
  };

  const { t } = useTranslation();

  const items = useSelector((state: any) => state.taskReducer.getIn(["CALENDAR", "children"]));

  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));

  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));

  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));

  // Track ActivityDialog open state to auto-refresh calendar when it closes
  const activityDialogOpen = useSelector((state: any) => state.activityReducer.get("show"));
  const prevActivityDialogOpen = useRef(false);

  const isGoogleConnected = appLinks
    ? (appLinks.toJS() as any[]).some((a) => a.app === "google" && a.connected === true)
    : false;

  const [googleOwnerEmail, setGoogleOwnerEmail] = useState<string | undefined>(undefined);

  const searchMessageResponse = useQuery({
    queryKey: ["searchMessages"],
    queryFn: () =>
      searchMessages({
        types: ["GMAIL"],
        filters: {},
      }),
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .fc-timegrid-slot {
        min-height: 35px !important;
        height: 35px !important;
      }
      .fc-timegrid-slot-lane {
        min-height: 35px !important;
        height: 35px !important;
      }
      .fc-timegrid-slot-label {
        min-height: 35px !important;
        height: 35px !important;
        display: flex !important;
        align-items: center !important;
      }
      .fc-timegrid-axis {
        min-height: 35px !important;
        height: 35px !important;
      }
      .fc-timegrid-axis-frame {
        min-height: 35px !important;
        height: 35px !important;
        display: flex !important;
        align-items: center !important;
      }
      .fc-timegrid-col-frame {
        min-height: 35px !important;
        height: 35px !important;
      }
      .fc-timegrid-slot-label-frame {
        min-height: 35px !important;
        height: 35px !important;
        display: flex !important;
        align-items: center !important;
      }
      .fc-timegrid-slot-label-cushion {
        min-height: 35px !important;
        height: 35px !important;
        padding: 0px !important;
        text-transform: uppercase !important;
        color: #6b7280 !important;
        padding-left: 3px !important;
      }
      
      .fc-daygrid-day-frame {
        position: relative !important;
      }
      
      .fc-daygrid-day-frame::before {
        content: "Click to add" !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        color: #9ca3af !important;
        font-size: 12px !important;
        opacity: 0 !important;
        transition: opacity 0.2s ease !important;
        pointer-events: none !important;
        z-index: 10 !important;
      }
      
      .fc-daygrid-day-frame:hover::before {
        opacity: 1 !important;
      }
      
      .fc-daygrid-day-events {
        min-height: 2em !important;
      }
      
      .fc-daygrid-day-events:empty::before {
        content: "Click to add" !important;
        display: block !important;
        text-align: center !important;
        color: #9ca3af !important;
        font-size: 12px !important;
        padding: 8px !important;
        opacity: 0 !important;
        transition: opacity 0.2s ease !important;
      }
      
      .fc-daygrid-day-events:empty:hover::before {
        opacity: 1 !important;
      }
        display: flex !important;
        align-items: center !important;
      }
      .fc-timegrid-slot-label-cushion {
        padding: 0px !important;
      }
      .line-clamp-2 {
        display: -webkit-box !important;
        -webkit-line-clamp: 2 !important;
        -webkit-box-orient: vertical !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isGoogleConnected) {
      setGoogleOwnerEmail(undefined);
      return;
    }
    getCurrentGoogleOwner()
      .then((owner) => setGoogleOwnerEmail(owner?.email))
      .catch(() => setGoogleOwnerEmail(undefined));
  }, [isGoogleConnected]);

  // Dynamically toggle today's background: amber on Month/Week, transparent on Day
  useEffect(() => {
    const id = "fc-today-highlight-style";
    let el = document.getElementById(id) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    if (currentView === "timeGridDay") {
      el.textContent = `
        .fc-day-today { background-color: transparent !important; }
        .fc-day-today .fc-daygrid-day-frame { background-color: transparent !important; }
      `;
    } else {
      el.textContent = `
        .fc-day-today { background-color: rgba(251, 191, 36, 0.08) !important; }
        .fc-day-today .fc-daygrid-day-frame { background-color: rgba(251, 191, 36, 0.08) !important; }
      `;
    }
    return () => {
      /* keep the element, just update content */
    };
  }, [currentView]);

  useEffect(() => {
    if (isGoogleConnected && dateRange?.start?.values?.length >= 2) {
      const startDate = format(new Date(dateRange.start.values[0]), "yyyy-MM-dd");
      const endDate = format(new Date(dateRange.start.values[1]), "yyyy-MM-dd");
      if (startDate && endDate) {
        onResyncCalendar(startDate, endDate);
      }
    } else {
      setGoogleEvents([]);
    }
  }, [isGoogleConnected, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRange != null) {
      onSearchTasks({ projectId, filters: dateRange, pageSize: 500 });
    }
  }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh calendar when ActivityDialog closes (new meeting created / updated)
  useEffect(() => {
    const wasOpen = prevActivityDialogOpen.current;
    prevActivityDialogOpen.current = activityDialogOpen;
    if (wasOpen && !activityDialogOpen) {
      // Dialog just closed — refresh to pick up newly created/edited events
      refreshCalendarData();
    }
  }, [activityDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCalendarData = () => {
    if (projectId) {
      return onSearchTasks({
        projectId,
        filters: dateRange || {},
        view: "CALENDAR",
        pageSize: 500,
        types: [ContentTypes.ACTIVITY],
      });
    }
    return Promise.resolve();
  };

  const getColor = (item) => {
    const itemType = item.get("itemType");
    const activityType = item.getIn(["event", "data", "activityType"]);

    if (activityType) {
      return "#E5EEF8";
    }

    switch (itemType) {
      case ContentTypes.SALE:
        return "#8ED9DF";
      case ContentTypes.CONTACT:
        return "#A0E9B0";
      case ContentTypes.COMPANY:
        return "#F9D392";
      default:
        return "#E5EEF8";
    }
  };

  const inboxItems = searchMessageResponse.isFetching ? [] : searchMessageResponse.data?.content;
  const inboxEvents = inboxItems.map((inboxItem) => {
    const inboxData = fromJS(inboxItem?.event?.data || {});
    return {
      source: "inbox",
      id: inboxItem.id,
      itemId: inboxItem.itemId,
      itemType: inboxItem.itemType,
      eventData: inboxItem.event || {},
      title: inboxData.getIn(["summary"]) || "(No subject)",
      start: `${inboxData.getIn(["start"])} ${inboxData.getIn(["startTime"])}`,
      end: `${inboxData.getIn(["end"])} ${inboxData.getIn(["endTime"])}`,
      editable: false,
      durationEditable: false,
      startEditable: false,
    } as any;
  });

  const calendarEvents = items
    .map((item) => {
      const timeZone = item.getIn(["event", "data", "timeZone"]);
      let start = item.getIn(["event", "data", "start"]);
      if (typeof start === "number") {
        start = formatInTimeZone(
          start,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      const startTime = item.getIn(["event", "data", "startTime"]);
      let end = item.getIn(["event", "data", "end"]);
      if (typeof end === "number") {
        end = formatInTimeZone(
          end,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      const endTime = item.getIn(["event", "data", "endTime"]);

      const isAllDay = startTime === "00:00:00" && endTime === "23:59:59";

      const id = item.get("id");
      const itemId = item.get("itemId");
      const itemType = item.get("itemType");
      const pending = pendingEventTimes[id];

      let startDateTime: any;
      let endDateTime: any;

      if (isAllDay) {
        startDateTime = start;
        endDateTime = end;
      } else {
        startDateTime = new Date(`${start}T${startTime}`);
        endDateTime = new Date(`${end}T${endTime}`);
      }

      const base = {
        source: "",
        id,
        itemId,
        itemType,
        eventData: item.getIn(["event"]) ? item.getIn(["event"]).toJS() : {},
        title:
          item.getIn(["event", "data", "item", "label"]) ||
          item.getIn(["event", "data", "summary"]) ||
          "(No title)",
        start: startDateTime,
        end: endDateTime,
        allDay: isAllDay,
        backgroundColor: getColor(item),
      } as any;

      if (pending) {
        return {
          ...base,
          start: pending.start,
          end: pending.end,
        };
      }

      return base;
    })
    .toJS();

  const adaptGoogleEvent = (gEvent: any) => {
    try {
      const id = `google:${gEvent.id || gEvent.iCalUID || Math.random()}`;
      const summary = gEvent.summary || "(No title)";
      const startMs = gEvent?.start?.dateTime?.value ?? gEvent?.start?.value;
      const endMs = gEvent?.end?.dateTime?.value ?? gEvent?.end?.value;
      const startDate = startMs ? new Date(startMs) : null;
      const endDate = endMs ? new Date(endMs) : null;

      if (!startDate || !endDate) return null;

      const isAllDay =
        gEvent?.start?.dateTime?.dateOnly === true || gEvent?.start?.dateOnly === true;

      const eventData = {
        data: {
          ...gEvent,
          summary,
          start: format(startDate, "yyyy-MM-dd"),
          end: format(endDate, "yyyy-MM-dd"),
          startTime: isAllDay ? "00:00:00" : format(startDate, "HH:mm:ss"),
          endTime: isAllDay ? "23:59:59" : format(endDate, "HH:mm:ss"),
          activityType: "meeting",
          item: { label: summary, value: id },
          googleCalendarLink: gEvent.htmlLink || gEvent.hangoutLink,
          source: "google",
          attendees: gEvent.attendees || [],
        },
      };

      const fcEvent = {
        source: "google",
        id,
        itemId: "",
        itemType: "",
        eventData,
        title: summary,
        start: isAllDay ? format(startDate, "yyyy-MM-dd") : startDate,
        end: isAllDay ? format(endDate, "yyyy-MM-dd") : endDate,
        allDay: !!isAllDay,
        backgroundColor: "#EAF2FF",
        editable: false,
        durationEditable: false,
      } as any;

      if (isAllDay) {
        fcEvent.end = format(addDays(endDate, 1), "yyyy-MM-dd");
      }

      return fcEvent;
    } catch {
      return null;
    }
  };

  const formatTimeLabel = (value: any) => {
    if (!value) return "";
    if (typeof value === "string") {
      if (value.includes(":")) {
        const [hours, minutes] = value.split(":").slice(0, 2);
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      }
      return value;
    }
    try {
      return format(value, "h:mm a");
    } catch {
      return String(value);
    }
  };

  const renderEventContent = (eventInfo) => {
    const source = eventInfo.event.extendedProps.source;
    const activityId = eventInfo.event.id;
    const itemId = eventInfo.event.extendedProps.itemId;
    const itemType = eventInfo.event.extendedProps.itemType;
    const eventData = eventInfo.event.extendedProps.eventData;
    const data = eventData?.data || {};
    const isGoogleEvent = source === "google";
    const isInboxEvent = source === "inbox";
    const isUpdating = updatingEvents.has(activityId);

    const assignees = data.assignees || [];

    let displayStartTime = data.startTime;
    let displayEndTime = data.endTime;

    if (isUpdating) {
      const newStart = eventInfo.event.start;
      const newEnd = eventInfo.event.end;

      if (newStart) {
        displayStartTime = format(newStart, "HH:mm:ss");
      }
      if (newEnd) {
        displayEndTime = format(newEnd, "HH:mm:ss");
      }
    }

    const startTime = displayStartTime ? new Date(`2000-01-01T${displayStartTime}`) : null;
    const endTime = displayEndTime ? new Date(`2000-01-01T${displayEndTime}`) : null;
    const durationMinutes =
      startTime && endTime
        ? Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))
        : 0;

    const activityData = {
      id: eventInfo.event.id,
      itemId,
      itemType,
      event: eventData,
    };

    const isAllDayEvent = displayStartTime === "00:00:00" && displayEndTime === "23:59:59";

    const timeRange = isAllDayEvent
      ? t("All day")
      : displayStartTime || displayEndTime
        ? `${formatTimeLabel(displayStartTime)}${displayEndTime ? ` → ${formatTimeLabel(displayEndTime)}` : ""}`
        : "";

    const ext = {
      type: data.activityType || "meeting",
      status: data.status || "scheduled",
    };
    const isCompleted = data.done === true;

    const getActivityIcon = (type) => {
      const iconClass = "w-3 h-3 inline mr-1";
      switch (type) {
        case ActivityTypes.email:
        case ActivityTypes.logEmail:
        case ActivityTypes.sendEmail:
        case ActivityTypes.sendDraftEmail:
        case ActivityTypes.sendScheduledEmail:
        case ActivityTypes.recordSentEmail:
          return <EnvelopeIcon className={iconClass} />;
        case ActivityTypes.task:
          return <ClockIcon className={iconClass} />;
        case ActivityTypes.meeting:
          return <CalendarDaysIcon className={iconClass} />;
        case ActivityTypes.deadline:
          return <FlagIcon className={iconClass} />;
        case ActivityTypes.lunch:
          return <FaceSmileIcon className={iconClass} />;
        case ActivityTypes.call:
          return <PhoneIcon className={iconClass} />;
        default:
          return <CalendarDaysIcon className={iconClass} />;
      }
    };

    const renderEventIcon = () => {
      const iconClass = "w-[14px] h-[14px] inline mr-[2px]";
      if (isGoogleEvent) {
        return (
          <img
            src={`${getBaseUrl()}/static/images/google-calendar.png`}
            className={`${iconClass} object-contain`}
            alt="Google Calendar"
          />
        );
      }
      if (
        data?.activityType === ActivityTypes.sendEmail ||
        data?.activityType === ActivityTypes.sendDraftEmail ||
        data?.activityType === ActivityTypes.sendScheduledEmail ||
        data?.activityType === ActivityTypes.recordSentEmail
      ) {
        if (data?.hasGmail) {
          return (
            <img
              src={`${getBaseUrl()}/static/images/gmail.png`}
              className={`${iconClass} object-contain`}
              alt="Gmail"
            />
          );
        }
        return <EnvelopeIcon className={iconClass} />;
      }
      return getActivityIcon(ext.type);
    };

    const renderCheckbox = (size = "w-4 h-4") => (
      <div className="items-center justify-end">
        {isGoogleEvent || isInboxEvent ? null : isCompleted ? (
          <div
            className={`${size} rounded-full flex items-center justify-center cursor-pointer transition-colors relative group`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActivityStatus(activityData, false);
            }}
          >
            <CheckCircleIcon className="w-4 h-4 text-green-500 hover:text-gray-300" />
            <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-[99999] pointer-events-none">
              {t("Completed")}
            </div>
          </div>
        ) : (
          <div
            className={`${size} bg-gray-200 rounded-full flex items-center justify-center cursor-pointer transition-colors relative group`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActivityStatus(activityData, true);
            }}
          >
            <CheckCircleIcon className="w-4 h-4 text-gray-400 hover:text-green-500" />
            <div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-[99999] pointer-events-none">
              {t("Mark as done")}
            </div>
          </div>
        )}
      </div>
    );

    const isCompactEvent =
      currentView === "dayGridMonth" ||
      durationMinutes <= 15 ||
      (eventInfo.el?.clientHeight && eventInfo.el.clientHeight < 40);

    if (isCompactEvent) {
      return (
        <div
          className="w-full flex items-center justify-center h-full cursor-pointer hover:opacity-80 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            setHoveredEvent(activityData);
            const position = calculateHoverPosition(e.clientX, e.clientY);
            setHoverPosition(position);
          }}
        >
          <div className="font-bold text-gray-700 flex-1 flex items-center text-xs overflow-hidden">
            <span className="flex-shrink-0 mr-1">{renderEventIcon()}</span>
            <span className="truncate min-w-0">{data.summary}</span>
          </div>
          {renderCheckbox()}
        </div>
      );
    }

    return (
      <div
        className="flex flex-col h-full cursor-pointer hover:opacity-80 transition-opacity w-full"
        onClick={(e) => {
          e.stopPropagation();
          setHoveredEvent(activityData);
          const position = calculateHoverPosition(e.clientX, e.clientY);
          setHoverPosition(position);
        }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="font-bold text-gray-700 flex-1 flex items-center text-sm overflow-hidden">
            <span className="flex-shrink-0 mr-1">{renderEventIcon()}</span>
            <span className="truncate min-w-0">{data.summary}</span>
          </div>
          {renderCheckbox("w-5 h-5")}
        </div>

        {timeRange && (
          <div
            className={`text-xs mt-0.5 flex ${isUpdating ? "text-blue-600 font-medium" : "text-gray-600"}`}
          >
            {timeRange}
            {isUpdating && <span className="ml-1 animate-pulse">●</span>}
          </div>
        )}

        <div className="flex items-center justify-between mt-auto mb-1">
          <div className="flex -space-x-1">
            {assignees &&
              assignees.length > 0 &&
              assignees.map((assignee: string, index: number) => {
                const assigneeUser =
                  members.find((member) => member.get("accountId") == assignee) ||
                  fromJS({ accountId: assignee, displayName: assignee });
                return (
                  <Avatar
                    key={index}
                    avatarUrl={assigneeUser.get("avatarUrl")}
                    displayName={assigneeUser.get("displayName")}
                    sizeIcon={5}
                  />
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  const onResyncCalendar = (_startDate?: string, _endDate?: string) => {
    const startDate = _startDate || format(new Date(dateRange?.start?.values?.[0]), "yyyy-MM-dd");
    const endDate = _endDate || format(new Date(dateRange?.start?.values?.[1]), "yyyy-MM-dd");

    if (googleFetchTimeoutRef.current) {
      clearTimeout(googleFetchTimeoutRef.current);
    }
    googleFetchTimeoutRef.current = setTimeout(() => {
      getGoogleCalendarEvents(startDate, endDate)
        .then((events) => {
          const adapted = (events || []).map((e: any) => adaptGoogleEvent(e)).filter(Boolean);
          setGoogleEvents(adapted as any);
        })
        .catch(() => setGoogleEvents([]));
    }, 200);
  };

  const handleDatesSet = (arg) => {
    setCurrentView(arg.view.type);
    const startDate = format(arg.start, "yyyy-MM-dd");
    const endDate = format(arg.end, "yyyy-MM-dd");
    const displayStart = arg.start as Date;
    const displayEnd = addDays(arg.end as Date, -1);
    let rangeLabel = "";
    if (isSameDay(displayStart, displayEnd)) {
      rangeLabel = format(displayStart, "EEEE, MMM d, yyyy");
    } else if (isSameYear(displayStart, displayEnd)) {
      rangeLabel = `${format(displayStart, "EEEE, MMM d")} - ${format(displayEnd, "MMM d, yyyy")}`;
    } else {
      rangeLabel = `${format(displayStart, "EEEE, MMM d")} - ${format(displayEnd, "MMM d, yyyy")}`;
    }
    setCurrentRangeLabel(rangeLabel);
    const dateRangeFilters = {
      ...taskFilters.toJS(),
      start: {
        operator: "dateRange",
        values: [`${startDate} 00:00:00`, `${endDate} 23:59:59`],
      },
    };
    setDateRange(dateRangeFilters);

    requestAnimationFrame(() => {
      const api = getApi();
      if (api && (arg.view.type === "timeGridWeek" || arg.view.type === "timeGridDay")) {
        api.scrollToTime(getRoundedTime());
      }
    });
  };

  const getApi = () => calendarRef.current?.getApi();
  const goPrev = () => getApi()?.prev();
  const goNext = () => getApi()?.next();
  const goToday = () => getApi()?.today();
  const changeView = (view: "dayGridMonth" | "timeGridWeek" | "timeGridDay") => {
    setCurrentView(view);
    getApi()?.changeView(view);
    if (view === "timeGridWeek" || view === "timeGridDay") {
      requestAnimationFrame(() => getApi()?.scrollToTime(getRoundedTime()));
    }
  };

  const openCreateActivity = (start: Date, end?: Date, isAllDay: boolean = false) => {
    let startTime, endTime, endDate;

    if (isAllDay) {
      startTime = "00:00:00";
      endTime = "23:59:59";
      endDate = format(start, "yyyy-MM-dd");
    } else {
      const safeEnd = end ?? addMinutes(start, 60);
      startTime = format(start, "HH:mm:ss");
      endTime = format(safeEnd, "HH:mm:ss");
      endDate = format(safeEnd, "yyyy-MM-dd");
    }

    dispatch(
      ActivityAction.showActivityDialog(null, null, {
        start: format(start, "yyyy-MM-dd"),
        end: endDate,
        startTime: startTime,
        endTime: endTime,
        activityType: ActivityTypes.meeting,
        summary: "Meeting",
      })
    );
  };

  const calculateHoverPosition = (clientX: number, clientY: number) => {
    const cardWidth = 400;
    const cardHeight = 300;
    const margin = 20;

    let x = clientX + 10;
    let y = clientY - 10;

    if (x + cardWidth + margin > window.innerWidth) {
      x = clientX - cardWidth - 10;
    }

    if (x < margin) {
      x = margin;
    }

    if (y + cardHeight + margin > window.innerHeight) {
      y = clientY - cardHeight - 10;
    }

    if (y < margin) {
      y = margin;
    }

    return { x, y };
  };

  const handleToggleActivityStatus = (activityData: any, newDoneState: boolean) => {
    const itemId = activityData?.itemId;
    const activityId = activityData?.id;

    if (!itemId || !activityId) {
      return;
    }

    markAsDoneActivity(itemId, activityId, newDoneState)
      .then(() => {
        if (projectId) {
          onSearchTasks({
            projectId,
            filters: dateRange || {},
            view: "CALENDAR",
            pageSize: 500,
            types: [ContentTypes.ACTIVITY],
          });
        }
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Error"),
            type: "error",
            message: t("Failed to update activity status"),
          })
        );
      });
  };

  const handleEventDragStart = (info: any) => {
    const activityId = info.event.id;
    const source = info.event.extendedProps?.source;
    if (String(activityId).startsWith("google:")) return;
    if (source === "inbox") return;
    setUpdatingEvents((prev) => new Set(prev).add(activityId));
  };

  const handleEventDragStop = () => {};

  const handleEventResizeStart = (info: any) => {
    const activityId = info.event.id;
    const source = info.event.extendedProps?.source;
    if (String(activityId).startsWith("google:")) return;
    if (source === "inbox") return;
    setUpdatingEvents((prev) => new Set(prev).add(activityId));
  };

  const handleEventResizeStop = () => {};

  const updateActivityDateTime = (
    activityId: string,
    newStart: Date,
    newEnd: Date,
    action: string,
    options?: { forceAllDay?: boolean }
  ) => {
    const forceAllDay = options?.forceAllDay === true;

    const start = format(newStart, "yyyy-MM-dd");
    const end = format(newEnd, "yyyy-MM-dd");
    const startTime = forceAllDay ? "00:00:00" : format(newStart, "HH:mm:ss");
    const endTime = forceAllDay ? "23:59:59" : format(newEnd, "HH:mm:ss");
    setUpdatingEvents((prev) => new Set(prev).add(activityId));
    updateActivity(activityId, { start, end, startTime, endTime })
      .then(() => {
        if (action === "resize" || action === "drop") {
          return refreshCalendarData();
        }
        return Promise.resolve();
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("Error"),
            type: "error",
            message: t("Failed to update activity"),
          })
        );
      })
      .finally(() => {
        setUpdatingEvents((prev) => {
          const newSet = new Set(prev);
          newSet.delete(activityId);
          return newSet;
        });
        setPendingEventTimes((prev) => {
          const rest = { ...prev };
          delete rest[activityId];
          return rest;
        });
      });
  };

  const handleEventDrop = (info: EventDropArg) => {
    const activityId = info.event.id;
    if (!activityId) return;
    if (String(activityId).startsWith("google:")) return;
    const source = info.event.extendedProps?.source;
    if (source === "inbox") return;

    const isAllDay = info.event.allDay === true;

    const rawStart = info.event.start;
    let rawEnd = info.event.end;

    if (!rawEnd) {
      rawEnd = addHours(rawStart, 1);
    }

    if (!rawStart || !rawEnd) {
      return;
    }

    if (isAllDay) {
      const startDateStr = format(rawStart, "yyyy-MM-dd");
      const endExclusiveDateStr = format(rawEnd, "yyyy-MM-dd");
      const endInclusiveDate = addDays(new Date(endExclusiveDateStr), -1);

      const normalizedStart = new Date(`${startDateStr}T00:00:00`);
      const normalizedEnd = new Date(`${format(endInclusiveDate, "yyyy-MM-dd")}T23:59:59`);

      setPendingEventTimes((prev) => ({
        ...prev,
        [activityId]: { start: normalizedStart, end: rawEnd },
      }));
      updateActivityDateTime(activityId, normalizedStart, normalizedEnd, "drop", {
        forceAllDay: true,
      });
      return;
    }

    setPendingEventTimes((prev) => ({
      ...prev,
      [activityId]: { start: rawStart, end: rawEnd },
    }));
    updateActivityDateTime(activityId, rawStart, rawEnd, "drop");
  };

  const handleEventResize = (info: EventResizeArg) => {
    const activityId = info.event.id;
    if (!activityId) return;
    if (String(activityId).startsWith("google:")) return;
    const source = info.event.extendedProps?.source;
    if (source === "inbox") return;

    const isAllDay = info.event.allDay === true;

    const rawStart = info.event.start;
    const rawEnd = info.event.end;
    if (!rawStart || !rawEnd) return;

    if (isAllDay) {
      const startDateStr = format(rawStart, "yyyy-MM-dd");
      const endExclusiveDateStr = format(rawEnd, "yyyy-MM-dd");
      const endInclusiveDate = addDays(new Date(endExclusiveDateStr), -1);

      const normalizedStart = new Date(`${startDateStr}T00:00:00`);
      const normalizedEnd = new Date(`${format(endInclusiveDate, "yyyy-MM-dd")}T23:59:59`);

      setPendingEventTimes((prev) => ({
        ...prev,
        [activityId]: { start: normalizedStart, end: rawEnd },
      }));
      updateActivityDateTime(activityId, normalizedStart, normalizedEnd, "resize", {
        forceAllDay: true,
      });
      return;
    }

    setPendingEventTimes((prev) => ({
      ...prev,
      [activityId]: { start: rawStart, end: rawEnd },
    }));
    updateActivityDateTime(activityId, rawStart, rawEnd, "resize");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hoverCardRef.current && !hoverCardRef.current.contains(event.target as Node)) {
        setHoveredEvent(null);
      }
    };

    if (hoveredEvent) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [hoveredEvent]);

  const containerHeight = height ?? "calc(90vh - 120px)";
  const rootStyle = fullHeight ? { height: "100%" } : {};

  const filteredGoogleEvents = googleEvents.filter((ge) => {
    const rawGoogleId = String(ge.id || "").replace(/^google:/, "");
    return !calendarEvents.some((ce) => {
      const appGoogleCalendarId = ce.eventData?.data?.googleCalendarId || "";
      return !!(rawGoogleId && appGoogleCalendarId && rawGoogleId === appGoogleCalendarId);
    });
  });

  return (
    <div className="w-full p-4" style={rootStyle}>
      <CalendarToolbar
        currentView={currentView}
        onViewChange={changeView}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
        allowedViews={allowedViews}
        hideViewTabs={hideViewTabs}
        calendarHref={calendarHref}
        onCalendarClick={onCalendarClick}
        dateRangeLabel={currentRangeLabel}
        onResyncCalendar={() => onResyncCalendar()}
        calendarConnected={isGoogleConnected}
        calendarConnectedEmail={googleOwnerEmail}
        appsSettingsHref={`/workspaces/${projectId}/settings/apps?key=Apps`}
      />

      <div className="calendar-container" style={{ height: containerHeight }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          headerToolbar={false}
          initialView={currentView}
          editable
          selectable
          selectMirror
          dayMaxEvents
          weekends
          initialDate={new Date()}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventDragStart={handleEventDragStart}
          eventDragStop={handleEventDragStop}
          eventResizeStart={handleEventResizeStart}
          eventResizeStop={handleEventResizeStop}
          datesSet={handleDatesSet}
          select={(selectionInfo) => {
            const start = selectionInfo.start;
            const end = selectionInfo.end;
            const isAllDay = selectionInfo.allDay;
            openCreateActivity(start, end, isAllDay);
          }}
          dateClick={() => {}}
          events={[...calendarEvents, ...filteredGoogleEvents, ...inboxEvents]}
          eventContent={renderEventContent}
          eventDidMount={(arg) => {
            const el = arg.el as HTMLElement;
            const isGoogleEvent = arg.event.extendedProps?.source === "google";
            const isInboxEvent = arg.event.extendedProps?.source === "inbox";

            el.style.borderRadius = "3px";
            el.style.border = "1px solid #fff";
            el.style.borderLeft = "2px solid #3B82F6";
            el.style.backgroundColor = arg.event.backgroundColor || "#E5EEF8";
            el.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
            el.style.padding = "0px";
            el.style.paddingLeft = "4px";
            el.style.overflow = "visible";
            el.style.zIndex = "1";

            if (isGoogleEvent || isInboxEvent) {
              el.style.opacity = "0.9";
              el.style.boxShadow = "0 1px 2px rgba(66, 133, 244, 0.15)";
            }

            if ((isGoogleEvent || isInboxEvent) && !isGoogleConnected) {
              el.style.display = "none";
            }
          }}
          scrollTime={getRoundedTime()}
          scrollTimeReset={true}
          expandRows={false}
          height="100%"
          eventClassNames={() => [
            "rounded-xl",
            "shadow-sm",
            "border",
            "border-black/10",
            "overflow-hidden",
            "px-1.5 py-1",
          ]}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          allDaySlot={true}
          slotDuration="00:15:00"
          slotLabelInterval="01:00:00"
          slotLaneClassNames={() => ["min-h-0"]}
          slotLabelClassNames={() => ["text-xs font-medium"]}
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            meridiem: "short",
          }}
          slotLabelFormat={{
            hour: "numeric",
            meridiem: "lowercase",
            hour12: true,
          }}
          allDayText={t("All day")}
          dayHeaderFormat={{ weekday: "short", day: "numeric" }}
          nowIndicator
        />
      </div>

      {hoveredEvent && (
        <div
          ref={hoverCardRef}
          className="fixed z-[9999]"
          style={{
            left: hoverPosition.x,
            top: hoverPosition.y,
          }}
        >
          <ActivityHoverCard
            activity={hoveredEvent}
            isVisible={true}
            onClose={() => setHoveredEvent(null)}
            onSearch={onSearchTasks}
          />
        </div>
      )}
    </div>
  );
}
